import {inject, Injectable} from '@angular/core';
import {FormArray, FormBuilder, FormControl, FormGroup, NonNullableFormBuilder, Validators} from '@angular/forms';
import {
  AddressDetail,
  ContactNumber,
  EmailAddress,
  Patient,
  PatientCreatePayload,
  PatientDoctor,
  PatientUpdatePayload,
  PatientWriteAddressDetail,
  PatientWriteContactNumber,
  PatientWriteDoctor,
  PatientWriteEmailAddress,
  PatientWriteSecondaryContact,
  SecondaryContact
} from '@xmedical/core';
import {CustomValidators} from '@xmedical/ui';

// Form-side shapes.
//
// The backend request DTO (CreatePatientRequestModel) accepts `label` on
// emails/contacts/addresses; the response/entity shape uses `description` for
// emails and contacts. So the form's `label` matches the wire on save, while
// hydrating from a loaded Patient reads `description` and writes it into `label`.
//
// Form-only fields that the wire DTO ignores or doesn't carry: `*Confirmation`
// re-entry fields. These exist purely for client-side validation.
//
// `status` is flattened to its id; `dateOfBirth`/`id`/`schemeId` permit the
// empty-string defaults the form starts with (datepickers/inputs replace them).
export interface ContactNumberFormShape {
  type: FormControl<number>;
  label: FormControl<string>;
  number: FormControl<string>;
}

export interface EmailAddressFormShape {
  type: FormControl<number>;
  label: FormControl<string>;
  emailAddress: FormControl<string>;
  emailAddressConfirm: FormControl<string>;
}

export interface AddressFormShape {
  type: FormControl<number>;
  label: FormControl<string>;
  address: FormControl<string>;
}

export interface DoctorFormShape {
  name: FormControl<string>;
  practiceNumber: FormControl<string>;
  tel: FormControl<string>;
  fax: FormControl<string>;
  email: FormControl<string>;
  preferredGreeting: FormControl<string>;
  preferredContactMethod: FormControl<string>;
}

export interface SecondaryContactFormShape {
  firstName: FormControl<string>;
  surname: FormControl<string>;
  relationToPatient: FormControl<string>;
  contactNumbers: FormArray<FormGroup<ContactNumberFormShape>>;
}

export interface MedicalAidFormShape {
  schemeId: FormControl<string | number>;
  scheme: FormControl<string>;
  plan: FormControl<string>;
  destinationCode: FormControl<string>;
  medicalAidNumber: FormControl<string>;
  dependantCode: FormControl<string>;
  mainMembersFirstName: FormControl<string>;
  mainMembersSurname: FormControl<string>;
}

export interface BillingContactFormShape {
  billingContactFirstName: FormControl<string>;
  billingContactSurname: FormControl<string>;
  billingContactIdentityNumber: FormControl<string>;
  billingContactIdentityNumberConfirmation: FormControl<string>;
  billingContactContactNumbers: FormArray<FormGroup<ContactNumberFormShape>>;
  billingContactAddressDetails: FormArray<FormGroup<AddressFormShape>>;
  billingContactEmailAddresses: FormArray<FormGroup<EmailAddressFormShape>>;
}

export interface PatientFormShape {
  id: FormControl<string | number>;
  status: FormControl<number | null>;
  title: FormControl<string>;
  firstName: FormControl<string>;
  surname: FormControl<string>;
  middleNames: FormControl<string>;
  nickname: FormControl<string>;
  identityNumber: FormControl<string>;
  identityNumberConfirmation: FormControl<string>;
  dateOfBirth: FormControl<string | Date>;
  gender: FormControl<string>;
  passportNumber: FormControl<string>;
  passportNumberConfirmation: FormControl<string>;
  homeLanguage: FormControl<string>;
  race: FormControl<string>;
  emailAddresses: FormArray<FormGroup<EmailAddressFormShape>>;
  contactNumbers: FormArray<FormGroup<ContactNumberFormShape>>;
  addressDetails: FormArray<FormGroup<AddressFormShape>>;
  familyDoctors: FormArray<FormGroup<DoctorFormShape>>;
  referringDoctors: FormArray<FormGroup<DoctorFormShape>>;
  secondaryContacts: FormArray<FormGroup<SecondaryContactFormShape>>;
  billingContact: FormGroup<BillingContactFormShape>;
  medicalAid: FormGroup<MedicalAidFormShape>;
  fileNumber: FormControl<string>;
  profilePictureUrl: FormControl<string>;
}

export type PatientForm = FormGroup<PatientFormShape>;
export type PatientFormValue = ReturnType<PatientForm['getRawValue']>;

// Practice-derived knobs the form needs at construction time. Passed in by the
// caller (form component reads them from inputs) so the service has no DI on
// CoreService — pure form-building logic.
export interface PatientFormSettings {
  useFileNumberAsPatientProfileId?: boolean;
  partialPatientFormValidationEnabled?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class PatientFormService {

  private formBuilder: NonNullableFormBuilder = inject(FormBuilder).nonNullable;
  // Nullable builder for the few controls that genuinely default to null (status, etc).
  private nullableFormBuilder = inject(FormBuilder);
  // Holds the active form between `new()` and `reset()`. Null is the lifecycle
  // gap before the first `new()` call and after `reset()` — callers that read
  // `form` outside that window are bugs and the typed nullable signature
  // forces them to handle it explicitly.
  private patientForm: PatientForm | null = null;

  get form(): PatientForm | null {
    return this.patientForm;
  }

  get hasScheme(): boolean {
    return this.requireForm().controls.medicalAid.controls.scheme.value !== '';
  }

  // Internal accessor for methods that *must* run against an active form.
  // Throws a clear error rather than letting a null leak surface as a deep
  // property access on undefined.
  private requireForm(): PatientForm {
    if (!this.patientForm) {
      throw new Error('PatientFormService: form accessed before new() / after reset()');
    }
    return this.patientForm;
  }

  private get addressDetails(): FormArray<FormGroup<AddressFormShape>> {
    return this.requireForm().controls.addressDetails;
  }

  private get emailAddresses(): FormArray<FormGroup<EmailAddressFormShape>> {
    return this.requireForm().controls.emailAddresses;
  }

  private get contactNumbers(): FormArray<FormGroup<ContactNumberFormShape>> {
    return this.requireForm().controls.contactNumbers;
  }

  private get secondaryContacts(): FormArray<FormGroup<SecondaryContactFormShape>> {
    return this.requireForm().controls.secondaryContacts;
  }

  private get familyDoctors(): FormArray<FormGroup<DoctorFormShape>> {
    return this.requireForm().controls.familyDoctors;
  }

  private get referringDoctors(): FormArray<FormGroup<DoctorFormShape>> {
    return this.requireForm().controls.referringDoctors;
  }

  private get billingContact(): FormGroup<BillingContactFormShape> {
    return this.requireForm().controls.billingContact;
  }

  private get billingContactContactNumbers(): FormArray<FormGroup<ContactNumberFormShape>> {
    return this.billingContact.controls.billingContactContactNumbers;
  }

  private get billingContactEmailAddresses(): FormArray<FormGroup<EmailAddressFormShape>> {
    return this.billingContact.controls.billingContactEmailAddresses;
  }

  private get billingContactAddressDetails(): FormArray<FormGroup<AddressFormShape>> {
    return this.billingContact.controls.billingContactAddressDetails;
  }

  reset() {
    this.patientForm = null;
  }

  new(patient?: Patient, settings: PatientFormSettings = {}): PatientForm {
    this.patientForm = this.newForm(settings);
    if (patient) {
      this.setData(patient);
    }
    return this.patientForm;
  }

  addAddress() {
    this.addressDetails.push(this.newAddressFormGroup());
  }

  removeAddress(index: number) {
    this.addressDetails.removeAt(index);
  }

  addEmailAddress() {
    this.emailAddresses.push(this.newEmailAddressFormGroup());
  }

  removeEmailAddress(index: number) {
    this.emailAddresses.removeAt(index);
  }

  addContactNumber() {
    this.contactNumbers.push(this.newContactNumberFormGroup());
  }

  removeContactNumber(index: number) {
    this.contactNumbers.removeAt(index);
  }

  addSecondaryContactPerson() {
    this.secondaryContacts.push(this.newSecondaryContactFormGroup());
  }

  removeSecondaryContactPerson(index: number) {
    this.secondaryContacts.removeAt(index);
  }

  addFamilyDoctor(doctor: PatientDoctor) {
    this.familyDoctors.push(this.newDoctorFormGroup(doctor));
  }

  addReferringDoctor(doctor: PatientDoctor) {
    this.referringDoctors.push(this.newDoctorFormGroup(doctor));
  }

  removeFamilyDoctor(index: number) {
    this.familyDoctors.removeAt(index);
  }

  removeReferringDoctor(index: number) {
    this.referringDoctors.removeAt(index);
  }

  addBillingContactAddress() {
    this.billingContactAddressDetails.push(this.newAddressFormGroup());
  }

  removeBillingContactAddress(index: number) {
    this.billingContactAddressDetails.removeAt(index);
  }

  addBillingContactEmailAddress() {
    this.billingContactEmailAddresses.push(this.newEmailAddressFormGroup());
  }

  removeBillingContactEmailAddress(index: number) {
    this.billingContactEmailAddresses.removeAt(index);
  }

  addBillingContactContactNumber() {
    this.billingContactContactNumbers.push(this.newContactNumberFormGroup());
  }

  removeBillingContactContactNumber(index: number) {
    this.billingContactContactNumbers.removeAt(index);
  }

  addSecondaryContactContactNumber(contactIndex: number) {
    this.secondaryContacts.controls[contactIndex].controls.contactNumbers.push(this.newContactNumberFormGroup());
  }

  removeSecondaryContactContactNumber(contactIndex: number, index: number) {
    this.secondaryContacts.controls[contactIndex].controls.contactNumbers.removeAt(index);
  }

  validateAllFormFields() {
    return CustomValidators.validateAllFormFields(this.requireForm());
  }

  printAllFormErrors(): any[] {
    return CustomValidators.printAllFormErrors(this.requireForm());
  }

  private newAddressFormGroup(address?: AddressDetail): FormGroup<AddressFormShape> {
    return makeAddressFormGroup(address);
  }

  private newEmailAddressFormGroup(emailAddress?: EmailAddress): FormGroup<EmailAddressFormShape> {
    return makeEmailAddressFormGroup(emailAddress);
  }

  private newContactNumberFormGroup(contactNumber?: ContactNumber, required = false): FormGroup<ContactNumberFormShape> {
    return makeContactNumberFormGroup(contactNumber, required);
  }

  private newSecondaryContactFormGroup(contactPerson?: SecondaryContact): FormGroup<SecondaryContactFormShape> {
    return makeSecondaryContactFormGroup(contactPerson);
  }

  private newDoctorFormGroup(doctor: PatientDoctor): FormGroup<DoctorFormShape> {
    return makeDoctorFormGroup(doctor);
  }

  private newForm(settings: PatientFormSettings): PatientForm {
    const fb = this.formBuilder;
    const nfb = this.nullableFormBuilder;
    const useFileNumberAsPatientProfileId = settings.useFileNumberAsPatientProfileId ?? false;
    const partialPatientFormValidationEnabled = settings.partialPatientFormValidationEnabled ?? false;

    const billingContact = fb.group<BillingContactFormShape>({
      billingContactFirstName: fb.control('', Validators.required),
      billingContactSurname: fb.control('', Validators.required),
      billingContactIdentityNumber: fb.control('', [CustomValidators.identityNumberValidator(true)]),
      billingContactIdentityNumberConfirmation: fb.control(''),
      billingContactContactNumbers: fb.array<FormGroup<ContactNumberFormShape>>([this.newContactNumberFormGroup()]),
      billingContactAddressDetails: fb.array<FormGroup<AddressFormShape>>([this.newAddressFormGroup()]),
      billingContactEmailAddresses: fb.array<FormGroup<EmailAddressFormShape>>([this.newEmailAddressFormGroup()]),
    }, {validators: [CustomValidators.duplicateFieldMatchRequired('billingContactIdentityNumber', 'billingContactIdentityNumberConfirmation')]});

    const medicalAid = fb.group<MedicalAidFormShape>({
      schemeId: fb.control<string | number>(''),
      scheme: fb.control(''),
      plan: fb.control(''),
      destinationCode: fb.control(''),
      medicalAidNumber: fb.control(''),
      dependantCode: fb.control(''),
      mainMembersFirstName: fb.control(''),
      mainMembersSurname: fb.control(''),
    });

    const form = new FormGroup<PatientFormShape>({
      id: fb.control<string | number>(''),
      status: nfb.control<number | null>(null),
      title: fb.control('', Validators.required),
      firstName: fb.control('', Validators.required),
      surname: fb.control('', Validators.required),
      middleNames: fb.control(''),
      nickname: fb.control(''),
      identityNumber: fb.control('', [CustomValidators.identityNumberValidator(true)]),
      identityNumberConfirmation: fb.control(''),
      dateOfBirth: fb.control<string | Date>('', Validators.required),
      gender: fb.control(''),
      passportNumber: fb.control(''),
      passportNumberConfirmation: fb.control(''),
      homeLanguage: fb.control(''),
      race: fb.control(''),
      emailAddresses: fb.array<FormGroup<EmailAddressFormShape>>([this.newEmailAddressFormGroup()]),
      contactNumbers: fb.array<FormGroup<ContactNumberFormShape>>([this.newContactNumberFormGroup()]),
      addressDetails: fb.array<FormGroup<AddressFormShape>>([this.newAddressFormGroup()]),
      familyDoctors: fb.array<FormGroup<DoctorFormShape>>([]),
      referringDoctors: fb.array<FormGroup<DoctorFormShape>>([]),
      secondaryContacts: fb.array<FormGroup<SecondaryContactFormShape>>([this.newSecondaryContactFormGroup()]),
      billingContact,
      medicalAid,
      fileNumber: fb.control('', useFileNumberAsPatientProfileId ? [Validators.required] : []),
      profilePictureUrl: fb.control(''),
    }, {validators: [
      CustomValidators.duplicateFieldMatchRequired('identityNumber', 'identityNumberConfirmation'),
      CustomValidators.duplicateFieldMatchRequired('passportNumber', 'passportNumberConfirmation')
    ]});

    if (!partialPatientFormValidationEnabled) {
      form.addValidators([CustomValidators.atLeastOneValueRequired(
        {formArrayName: 'contactNumbers', formControlName: 'number'},
        {formArrayName: 'addressDetails', formControlName: 'address'},
        {formArrayName: 'emailAddresses', formControlName: 'emailAddress'})]);
      billingContact.addValidators([CustomValidators.atLeastOneValueRequired(
        {formArrayName: 'billingContactContactNumbers', formControlName: 'number'},
        {formArrayName: 'billingContactAddressDetails', formControlName: 'address'},
        {formArrayName: 'billingContactEmailAddresses', formControlName: 'emailAddress'})]);
    }
    return form;
  }

  private setData(patient: Patient) {
    if (!patient) { return; }
    this.requireForm().patchValue({
      id: patient.id,
      status: patient.status?.id ?? null,
      title: patient.title,
      firstName: patient.firstName,
      surname: patient.surname,
      middleNames: patient.middleNames,
      nickname: patient.nickname,
      identityNumber: patient.identityNumber,
      identityNumberConfirmation: patient.identityNumber,
      dateOfBirth: patient.dateOfBirth,
      gender: patient.gender,
      passportNumber: patient.passportNumber,
      passportNumberConfirmation: patient.passportNumber,
      homeLanguage: patient.homeLanguage,
      race: patient.race,
      fileNumber: patient.fileNumber,
      profilePictureUrl: patient.profilePictureUrl,
    });
    if (patient.billingContact) {
      this.billingContact.patchValue({
        billingContactFirstName: patient.billingContact.billingContactFirstName,
        billingContactSurname: patient.billingContact.billingContactSurname,
        billingContactIdentityNumber: patient.billingContact.billingContactIdentityNumber,
        billingContactIdentityNumberConfirmation: patient.billingContact.billingContactIdentityNumber,
      });
    }
    if (patient.medicalAid) {
      this.requireForm().controls.medicalAid.patchValue({
        schemeId: patient.medicalAid.schemeId,
        scheme: patient.medicalAid.scheme,
        plan: patient.medicalAid.plan,
        destinationCode: patient.medicalAid.destinationCode,
        medicalAidNumber: patient.medicalAid.medicalAidNumber,
        dependantCode: patient.medicalAid.dependantCode,
        mainMembersFirstName: patient.medicalAid.mainMembersFirstName,
        mainMembersSurname: patient.medicalAid.mainMembersSurname,
      });
    }

    // FormArrays cannot be patchValue'd wholesale (patchValue only updates
    // existing controls; missing entries are silently ignored). Use
    // hydrateArray to clear, filter, and rebuild from the source list.
    this.hydrateArray(this.familyDoctors, patient.familyDoctors,
      d => this.newDoctorFormGroup(d));
    this.hydrateArray(this.referringDoctors, patient.referringDoctors,
      d => this.newDoctorFormGroup(d));

    this.hydrateArray(this.contactNumbers, patient.contactNumbers,
      c => this.newContactNumberFormGroup(c),
      { filter: hasContactValue, ensureOne: () => this.newContactNumberFormGroup() });
    this.hydrateArray(this.emailAddresses, patient.emailAddresses,
      e => this.newEmailAddressFormGroup(e),
      { filter: hasEmailValue, ensureOne: () => this.newEmailAddressFormGroup() });
    this.hydrateArray(this.addressDetails, patient.addressDetails,
      a => this.newAddressFormGroup(a),
      { filter: hasAddressValue, ensureOne: () => this.newAddressFormGroup() });

    if (patient.billingContact) {
      this.hydrateArray(this.billingContactContactNumbers, patient.billingContact.billingContactContactNumbers,
        c => this.newContactNumberFormGroup(c),
        { filter: hasContactValue, ensureOne: () => this.newContactNumberFormGroup() });
      this.hydrateArray(this.billingContactEmailAddresses, patient.billingContact.billingContactEmailAddresses,
        e => this.newEmailAddressFormGroup(e),
        { filter: hasEmailValue, ensureOne: () => this.newEmailAddressFormGroup() });
      this.hydrateArray(this.billingContactAddressDetails, patient.billingContact.billingContactAddressDetails,
        a => this.newAddressFormGroup(a),
        { filter: hasAddressValue, ensureOne: () => this.newAddressFormGroup() });
    }

    this.hydrateArray(this.secondaryContacts, patient.secondaryContacts,
      s => this.newSecondaryContactFormGroup(s));

    this.requireForm().updateValueAndValidity();
  }

  // Rebuild a FormArray from a source list:
  // - No source list (undefined / empty) → leave the form's existing default
  //   rows in place. This preserves the "form starts with one empty row"
  //   behaviour for create flows while still allowing edit flows to overwrite.
  // - Source present → clear the FormArray, optionally filter the source, and
  //   push one fresh group per surviving item via the factory.
  // - `ensureOne` factory (when supplied) pushes a single default group if
  //   the filter eliminated every entry, so contact/email/address sections
  //   never render with zero rows.
  private hydrateArray<TItem, TGroup extends FormGroup<any>>(
    formArray: FormArray<TGroup>,
    source: TItem[] | undefined,
    factory: (item: TItem) => TGroup,
    options: { filter?: (item: TItem) => boolean; ensureOne?: () => TGroup } = {},
  ): void {
    if (!source?.length) {
      return;
    }
    formArray.clear();
    const usable = options.filter ? source.filter(options.filter) : source;
    for (const item of usable) {
      formArray.push(factory(item));
    }
    if (options.ensureOne && formArray.length === 0) {
      formArray.push(options.ensureOne());
    }
  }
}

// Exported group factories. Each returns a fully typed FormGroup with the
// same default values, validators, and label-translation rules used during
// initial form construction. Child fieldsets call these directly to push new
// rows onto their bound FormArrays without depending on the singleton service.

export function makeDoctorFormGroup(doctor?: PatientDoctor): FormGroup<DoctorFormShape> {
  return new FormGroup<DoctorFormShape>({
    name: new FormControl(doctor?.name ?? '', { nonNullable: true }),
    practiceNumber: new FormControl(doctor?.practiceNumber ?? '', { nonNullable: true }),
    tel: new FormControl(doctor?.tel ?? '', { nonNullable: true }),
    fax: new FormControl(doctor?.fax ?? '', { nonNullable: true }),
    email: new FormControl(doctor?.email ?? '', { nonNullable: true }),
    preferredGreeting: new FormControl(doctor?.preferredGreeting ?? '', { nonNullable: true }),
    preferredContactMethod: new FormControl(doctor?.preferredContactMethod ?? '', { nonNullable: true }),
  });
}

export function makeAddressFormGroup(address?: AddressDetail): FormGroup<AddressFormShape> {
  return new FormGroup<AddressFormShape>({
    type: new FormControl(address?.type ?? 2, { nonNullable: true, validators: Validators.required }),
    label: new FormControl(address?.label ?? 'Physical', { nonNullable: true, validators: Validators.required }),
    address: new FormControl(address?.address ?? '', { nonNullable: true }),
  });
}

// Wire-side `description` is mapped to form-side `label` on construction.
export function makeEmailAddressFormGroup(emailAddress?: EmailAddress): FormGroup<EmailAddressFormShape> {
  return new FormGroup<EmailAddressFormShape>({
    type: new FormControl(emailAddress?.type ?? 0, { nonNullable: true, validators: Validators.required }),
    label: new FormControl(emailAddress?.description ?? 'Home', { nonNullable: true, validators: Validators.required }),
    emailAddress: new FormControl(emailAddress?.emailAddress ?? '', { nonNullable: true, validators: [Validators.email] }),
    emailAddressConfirm: new FormControl(emailAddress?.emailAddress ?? '', { nonNullable: true }),
  }, { validators: [CustomValidators.duplicateFieldMatchRequired('emailAddress', 'emailAddressConfirm')] });
}

// Wire-side `description` is mapped to form-side `label` on construction.
export function makeContactNumberFormGroup(contactNumber?: ContactNumber, required = false): FormGroup<ContactNumberFormShape> {
  const group = new FormGroup<ContactNumberFormShape>({
    type: new FormControl(contactNumber?.type ?? 2, { nonNullable: true, validators: Validators.required }),
    label: new FormControl(contactNumber?.description ?? 'Cell', { nonNullable: true, validators: Validators.required }),
    number: new FormControl(contactNumber?.number ?? '', { nonNullable: true, validators: [Validators.pattern('[0-9 ]*'), Validators.minLength(10)] }),
  });
  if (required) {
    group.controls.number.addValidators(Validators.required);
  }
  return group;
}

export function makeSecondaryContactFormGroup(contactPerson?: SecondaryContact): FormGroup<SecondaryContactFormShape> {
  const contactNumbers = new FormArray<FormGroup<ContactNumberFormShape>>([]);
  if (contactPerson?.contactNumbers?.length) {
    contactPerson.contactNumbers.forEach(c => contactNumbers.push(makeContactNumberFormGroup(c)));
  } else {
    contactNumbers.push(makeContactNumberFormGroup());
  }
  return new FormGroup<SecondaryContactFormShape>({
    firstName: new FormControl(contactPerson?.firstName ?? '', { nonNullable: true }),
    surname: new FormControl(contactPerson?.surname ?? '', { nonNullable: true }),
    relationToPatient: new FormControl(contactPerson?.relation ?? '', { nonNullable: true }),
    contactNumbers,
  });
}

// Used during edit-mode hydration: drop entries that have neither a usable
// type nor any user-provided value. Matches the prior inline filters.
function hasContactValue(c: ContactNumber | null | undefined): boolean {
  return !!c && (c.type !== null || !!c.number);
}

function hasEmailValue(e: EmailAddress | null | undefined): boolean {
  return !!e && (e.type !== null || !!e.emailAddress);
}

function hasAddressValue(a: AddressDetail | null | undefined): boolean {
  return !!a && (a.type !== null || !!a.address);
}

// Honest write-payload mappers (Phase 4 of patient-store-migration). No
// `as unknown as ...` casts — the wire shapes (PatientWriteEmailAddress etc.)
// use `label`, matching the form, so values flow through cleanly.
//
// `formValueToCreatePayload`: server defaults status / isFlagged / flaggedDate
// on POST, so they're absent.
// `formValueToUpdatePayload`: pulls the flag fields from the existing patient
// (the form does not carry them) and sends both `status` and `statusId` with
// the same value (the wire DTO requires both, not nullable).
//
// Date normalisation happens inside the mapper — `dateOfBirth` is a string
// when the form is hydrated from JSON localStorage, a Date when freshly
// edited via the datepicker.

function toWriteEmail(e: ReturnType<FormGroup<EmailAddressFormShape>['getRawValue']>): PatientWriteEmailAddress {
  return { type: e.type, label: e.label, emailAddress: e.emailAddress };
}

function toWriteContact(c: ReturnType<FormGroup<ContactNumberFormShape>['getRawValue']>): PatientWriteContactNumber {
  return { type: c.type, label: c.label, number: c.number };
}

function toWriteAddress(a: ReturnType<FormGroup<AddressFormShape>['getRawValue']>): PatientWriteAddressDetail {
  return { type: a.type, label: a.label, address: a.address };
}

function toWriteSecondary(s: ReturnType<FormGroup<SecondaryContactFormShape>['getRawValue']>): PatientWriteSecondaryContact {
  return {
    firstName: s.firstName,
    surname: s.surname,
    relationToPatient: s.relationToPatient,
    contactNumbers: (s.contactNumbers ?? []).map(toWriteContact),
    emailAddresses: [],
    addressDetails: [],
  };
}

function toWriteDoctor(d: ReturnType<FormGroup<DoctorFormShape>['getRawValue']>): PatientWriteDoctor {
  return {
    name: d.name,
    practiceNumber: d.practiceNumber,
    tel: d.tel,
    fax: d.fax,
    email: d.email,
    preferredGreeting: d.preferredGreeting,
    preferredContactMethod: d.preferredContactMethod,
  };
}

function normaliseDateOfBirth(value: string | Date): Date {
  if (value instanceof Date) return value;
  return new Date(value);
}

function buildCommonPayload(value: PatientFormValue) {
  return {
    title: value.title,
    firstName: value.firstName,
    surname: value.surname,
    middleNames: value.middleNames,
    nickname: value.nickname,
    identityNumber: value.identityNumber,
    dateOfBirth: normaliseDateOfBirth(value.dateOfBirth),
    gender: value.gender,
    passportNumber: value.passportNumber,
    homeLanguage: value.homeLanguage,
    race: value.race,
    fileNumber: value.fileNumber,
    profilePictureUrl: value.profilePictureUrl,
    emailAddresses: (value.emailAddresses ?? []).map(toWriteEmail),
    contactNumbers: (value.contactNumbers ?? []).map(toWriteContact),
    addressDetails: (value.addressDetails ?? []).map(toWriteAddress),
    secondaryContacts: (value.secondaryContacts ?? []).map(toWriteSecondary),
    familyDoctors: (value.familyDoctors ?? []).map(toWriteDoctor),
    referringDoctors: (value.referringDoctors ?? []).map(toWriteDoctor),
    billingContact: {
      billingContactFirstName: value.billingContact.billingContactFirstName,
      billingContactSurname: value.billingContact.billingContactSurname,
      billingContactIdentityNumber: value.billingContact.billingContactIdentityNumber,
      billingContactContactNumbers: (value.billingContact.billingContactContactNumbers ?? []).map(toWriteContact),
      billingContactEmailAddresses: (value.billingContact.billingContactEmailAddresses ?? []).map(toWriteEmail),
      billingContactAddressDetails: (value.billingContact.billingContactAddressDetails ?? []).map(toWriteAddress),
    },
    medicalAid: {
      schemeId: typeof value.medicalAid.schemeId === 'number'
        ? value.medicalAid.schemeId
        : (value.medicalAid.schemeId === '' ? 0 : Number(value.medicalAid.schemeId)),
      scheme: value.medicalAid.scheme,
      plan: value.medicalAid.plan,
      destinationCode: value.medicalAid.destinationCode,
      medicalAidNumber: value.medicalAid.medicalAidNumber,
      dependantCode: value.medicalAid.dependantCode,
      mainMembersFirstName: value.medicalAid.mainMembersFirstName,
      mainMembersSurname: value.medicalAid.mainMembersSurname,
    },
  };
}

export function formValueToCreatePayload(value: PatientFormValue): PatientCreatePayload {
  return buildCommonPayload(value);
}

export function formValueToUpdatePayload(value: PatientFormValue, patient: Patient): PatientUpdatePayload {
  const statusId = value.status ?? patient.statusId ?? 0;
  return {
    ...buildCommonPayload(value),
    status: statusId,
    statusId,
    isFlagged: patient.isFlagged ?? false,
    flaggedComment: patient.flaggedComment ?? '',
  };
}
